import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import Card from "@mui/material/Card";
import React from "react";

import MDBox from "@/components/atoms/MDBox";
import { MDButton } from "@/components/atoms/MDButton";
import MDTypography from "@/components/atoms/MDTypography";

interface Badge {
  color: "primary" | "secondary" | "info" | "success" | "warning" | "error" | "light" | "dark";
  label: string;
}

interface Price {
  currency: string;
  value: string;
  type: string;
}

interface Specification {
  label: string;
  includes: boolean;
  focus?: boolean;
  format?: string;
}

interface Action {
  type: "internal" | "external";
  route: string;
  label: string;
  color: "primary" | "secondary" | "info" | "success" | "warning" | "error" | "light" | "dark";
  variant?: "text" | "contained" | "outlined" | "gradient";
  onClick?: () => void;
}
interface Props {
  color?:
    | "primary"
    | "secondary"
    | "info"
    | "success"
    | "warning"
    | "error"
    | "light"
    | "dark"
    | "white";
  badge: Badge;
  price: Price;
  specifications: Specification[];
  action: Action;
  isPlanContracted?: boolean;
  shadow?: boolean;
}

const DefaultPricingCard = ({
  color = "white",
  badge,
  price,
  specifications,
  action,
  isPlanContracted,
}: Props) => {
  const renderSpecifications = specifications.map(({ label, focus, format }) => {
    // Convert different line break formats to actual line breaks
    const formatText = (text: string) => {
      // Handle different line break patterns: \n, \\n, \r\n, etc.
      const lines = text
        .replace(/\\\\n/g, '\n')  // Convert \\n to \n
        .replace(/\\n/g, '\n')    // Convert \n to actual newline
        .split(/\r?\n/);          // Split on actual line breaks
      
      return lines.map((line, index, array) => (
        <React.Fragment key={index}>
          {line.trim()}
          {index < array.length - 1 && <br />}
        </React.Fragment>
      ));
    };
    
    return (
      <MDBox key={label} display="flex" alignItems="center" justifyContent="center" px={0.5} py={focus ? 1 : 0.5}>
        <MDBox
          display="flex"
          justifyContent="center"
          alignItems="center"
          width="1.5rem"
          mr={2}
          mt={-0.125}
          padding={0}
          bgcolor={focus ? "dark" : "transparent"}
        ></MDBox>
        <MDTypography
          variant="body2"
          color={color === "white" ? "text" : "white"}
          fontWeight={focus ? "bold" : "regular"}
          mt={focus ? -2.5 : 0}
          textAlign={focus ? "center" : "left"}
        >
          {focus && format} {formatText(label)}
        </MDTypography>
      </MDBox>
    );
  });

  return (
    <Card sx={{ height: "100%", boxShadow: "none" }}>
      <MDBox
        bgColor={color}
        variant={color === "white" ? "contained" : "gradient"}
        borderRadius="xl"
        sx={{ height: "100%", display: "flex", flexDirection: "column" }}
      >
        <MDBox
          bgColor={badge.color}
          width="max-content"
          px={3}
          pt={0}
          pb={0.5}
          mx="auto"
          mt={-1.375}
          borderRadius="section"
          lineHeight={1}
        >
          <MDTypography
            variant="caption"
            textTransform="uppercase"
            fontWeight="medium"
            color={badge.color === "light" ? "dark" : "white"}
          >
            {badge.label}
          </MDTypography>
        </MDBox>
        <MDBox pt={2} pb={1} px={2} textAlign="center">
          <MDBox my={1}>
            <MDTypography variant="h2" color={color === "white" ? "dark" : "white"}>
              <MDTypography
                display="inline"
                component="small"
                variant="h6"
                color="inherit"
                verticalAlign="top"
              >
                {price.currency}
              </MDTypography>
              {price.value}
              <MDTypography display="inline" component="small" variant="h6" color="inherit">
                /{price.type}
              </MDTypography>
            </MDTypography>
          </MDBox>
        </MDBox>
        <MDBox pb={2} px={2} sx={{ flex: 1, display: "flex", flexDirection: "column" }}>
          {renderSpecifications}
          <MDBox mt="auto">
            <MDButton
              disabled={isPlanContracted}
              variant={action.variant}
              color={action.color}
              fullWidth
              onClick={action.onClick}
            >
              {action.label}&nbsp;
              {!isPlanContracted && <ArrowForwardIcon />}
            </MDButton>
          </MDBox>
        </MDBox>
      </MDBox>
    </Card>
  );
};

export default DefaultPricingCard;
